/**
 * Pilot flag — gradual rollout of a feature to a list of people / roles /
 * designations, with a one-way "enable for everyone" cutover.
 *
 * One Firestore doc per feature under `settings/{docId}`:
 *   { generalAvailability, pilotUserIds, pilotRoles, pilotDesignations, updatedAt, … }
 *
 * `createPilotFlag(docId)` returns the parse / gate / subscribe / save helpers and a
 * React hook bound to that doc. Fails closed: no doc / read error = off.
 *
 * Each flag's rules block must allow reads for its audience and admin-only writes
 * (see `settings/files_redesign` / `settings/rfq_requests` in firestore.rules).
 */

import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import type { CRMUser, EmployeeDesignation, User, UserRole } from '../types';
import { listReleaseNotifyEmployees, parseAudienceUserIds, releaseMatchesUserAudience } from './releaseAudience';

export interface PilotFlag {
  /** Cutover switch — when true everyone gets the feature and the pilot lists are ignored. */
  generalAvailability: boolean;
  pilotUserIds: string[];
  pilotRoles: UserRole[];
  pilotDesignations: EmployeeDesignation[];
  updatedAt: Date | null;
  updatedByUid: string | null;
  updatedByName: string | null;
  /** When `generalAvailability` was first switched on (drives the "remove me" notice). */
  generalAvailabilitySince: Date | null;
}

export const EMPTY_PILOT_FLAG: PilotFlag = {
  generalAvailability: false,
  pilotUserIds: [],
  pilotRoles: [],
  pilotDesignations: [],
  updatedAt: null,
  updatedByUid: null,
  updatedByName: null,
  generalAvailabilitySince: null,
};

const USER_ROLES: ReadonlySet<string> = new Set<UserRole>([
  'employee',
  'employee_shop',
  'employee_office',
  'client',
  'admin',
  'designer',
  'architect',
  'engineer',
  'staff',
  'supplier',
  'cpa',
  'subcontractor',
]);
const DESIGNATIONS: ReadonlySet<string> = new Set<EmployeeDesignation>([
  'project_manager',
  'estimator',
  'executive_assistant',
  'sales_rep',
  'cfo',
  'coo',
  'office_manager',
]);

function toDate(v: unknown): Date | null {
  const d = (v as { toDate?: () => Date } | undefined)?.toDate?.();
  return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
}

export function parsePilotFlag(data: Record<string, unknown> | undefined | null): PilotFlag {
  if (!data) return EMPTY_PILOT_FLAG;
  const roles = Array.isArray(data.pilotRoles)
    ? (data.pilotRoles.filter((r): r is UserRole => typeof r === 'string' && USER_ROLES.has(r)) as UserRole[])
    : [];
  const designations = Array.isArray(data.pilotDesignations)
    ? (data.pilotDesignations.filter(
        (d): d is EmployeeDesignation => typeof d === 'string' && DESIGNATIONS.has(d),
      ) as EmployeeDesignation[])
    : [];
  return {
    generalAvailability: data.generalAvailability === true,
    pilotUserIds: parseAudienceUserIds(data.pilotUserIds),
    pilotRoles: roles,
    pilotDesignations: designations,
    updatedAt: toDate(data.updatedAt),
    updatedByUid: typeof data.updatedByUid === 'string' ? data.updatedByUid : null,
    updatedByName: typeof data.updatedByName === 'string' ? data.updatedByName : null,
    generalAvailabilitySince: toDate(data.generalAvailabilitySince),
  };
}

/** Pure: does this user get the feature? */
export function isPilotFlagEnabled(
  flag: PilotFlag | null | undefined,
  user: Pick<User, 'id' | 'role' | 'designations'> | null | undefined,
): boolean {
  if (!flag) return false;
  if (flag.generalAvailability) return true;
  if (!user) return false;
  return releaseMatchesUserAudience(
    {
      audienceRoles: flag.pilotRoles,
      audienceDesignations: flag.pilotDesignations,
      audienceUserIds: flag.pilotUserIds,
    },
    user as User,
  );
}

/**
 * People who can be enrolled by name: staff plus designers / architects / engineers
 * (they all use Project Hub). Keeps test accounts so QA logins can pilot; drops
 * shared workstations.
 */
export const PILOT_PEOPLE_OPTIONS = { includeTestUsers: true, includeDesignPartners: true } as const;

export function listPilotCandidates(crmUsers: CRMUser[]): CRMUser[] {
  return listReleaseNotifyEmployees(crmUsers, PILOT_PEOPLE_OPTIONS);
}

export interface SavePilotFlagPatch {
  generalAvailability?: boolean;
  pilotUserIds?: string[];
  pilotRoles?: UserRole[];
  pilotDesignations?: EmployeeDesignation[];
}

export interface PilotFlagAccess {
  /** True once the first snapshot (or error) has arrived. Treat `enabled` as false until then. */
  loaded: boolean;
  enabled: boolean;
  flag: PilotFlag;
}

export interface PilotFlagHandle {
  docId: string;
  subscribe: (onData: (flag: PilotFlag, exists: boolean) => void, onError?: (err: Error) => void) => () => void;
  save: (patch: SavePilotFlagPatch, actor: { uid: string; name: string }, current?: PilotFlag | null) => Promise<void>;
  /** Live gate for the signed-in user. One subscription per mounted consumer. */
  useAccess: () => PilotFlagAccess;
}

export function createPilotFlag(docId: string): PilotFlagHandle {
  const flagRef = () => doc(db, 'settings', docId);

  const subscribe: PilotFlagHandle['subscribe'] = (onData, onError) =>
    onSnapshot(
      flagRef(),
      snap => onData(parsePilotFlag(snap.data() as Record<string, unknown> | undefined), snap.exists()),
      err => {
        console.warn(`[pilotFlag:${docId}] subscribe`, err);
        onError?.(err);
        onData(EMPTY_PILOT_FLAG, false);
      },
    );

  const save: PilotFlagHandle['save'] = async (patch, actor, current) => {
    const data: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
      updatedByUid: actor.uid,
      updatedByName: actor.name,
    };
    if (patch.generalAvailability !== undefined) {
      data.generalAvailability = patch.generalAvailability;
      if (patch.generalAvailability && !current?.generalAvailabilitySince) data.generalAvailabilitySince = serverTimestamp();
      if (!patch.generalAvailability) data.generalAvailabilitySince = null;
    }
    if (patch.pilotUserIds) data.pilotUserIds = Array.from(new Set(patch.pilotUserIds.filter(Boolean)));
    if (patch.pilotRoles) data.pilotRoles = Array.from(new Set(patch.pilotRoles));
    if (patch.pilotDesignations) data.pilotDesignations = Array.from(new Set(patch.pilotDesignations));
    await setDoc(flagRef(), data, { merge: true });
  };

  function useAccess(): PilotFlagAccess {
    const { user } = useAuth();
    const [flag, setFlag] = useState<PilotFlag>(EMPTY_PILOT_FLAG);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
      if (!user) {
        setFlag(EMPTY_PILOT_FLAG);
        setLoaded(true);
        return undefined;
      }
      setLoaded(false);
      return subscribe(next => {
        setFlag(next);
        setLoaded(true);
      });
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const enabled = useMemo(() => loaded && isPilotFlagEnabled(flag, user), [loaded, flag, user]);
    return { loaded, enabled, flag };
  }

  return { docId, subscribe, save, useAccess };
}
